// CORE
import type { Map as ImMap, RecordOf } from 'immutable';
import * as React from 'react';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
// MUI
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { Theme } from '@mui/material/styles';
// DATA
import { balanceSelectors } from 'app-components/flux/calculated-balances';
import { goalsSelectors } from 'app-components/flux/goals';
import type { Goal } from 'app-components/flux/goals/goalsTypes';
import { useAppSelector } from 'hooks/redux';
import { makeStyles } from 'tss-react/mui';

// SHARED/LOCAL
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import InfoTooltipIcon from 'components/InfoTooltipIcon';
import QAmountField from 'components/QAmountField';
import QButton from 'components/QButton';
import AmountField from 'components/QuickenControls/AmountField';
import type { FundingAccount } from '../types';
import { AddGoalContext } from './state';

const useStyles = makeStyles()((theme: Theme) => ({
  goalAmountRoot: {
    display: 'flex',
    padding: `${theme.spacing(3)} ${theme.spacing(6)} 0 ${theme.spacing(1)}`,
    flexDirection: 'row',
    flexGrow: 1,
    height: '100%',
    overflow: 'hidden',
  },
  leftPanel: {
    height: 128,
    width: 'auto',
  },
  rightPanel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    flex: 3,
    padding: `0 ${theme.spacing(4)} ${theme.spacing(1)} ${theme.spacing(2)}`,
    flexGrow: 1,
    overflowY: 'auto',
    maxHeight: 'calc(90vh + 60px)', // Space available to right panel with the addition of space taken up by stdDialog footer
    minHeight: 0,
    overflowX: 'hidden',
    '& input': {
      MozAppearance: 'textfield',
      '&::-webkit-inner-spin-button, &::-webkit-outer-spin-button': {
        WebkitAppearance: 'none',
        margin: 0,
      },
    },
  },
  nameField: {
    marginTop: theme.spacing(2),
    minWidth: 200,
    marginBottom: theme.spacing(2),
    width: '80%',
  },
  amountField: {
    minWidth: 168,
  },
  divider: {
    margin: `${theme.spacing(1)} ${theme.spacing(6)} ${theme.spacing(3)} 0`,
  },
  savedSoFarWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: '16px',
    width: '100%',
    marginTop: theme.spacing(2),
  },
  savedSoFarLine: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: '16px',
    width: '90%',
  },
  accountDropdownField: {
    flexBasis: '100%',
  },
  accountDropdownItem: {
    width: '100%',
  },
  accountRow: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  accountDropdownMenu: {
    borderRadius: theme.shape.borderRadius * 2,
  },
  accountAmount: {
    marginLeft: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  deleteButton: {
    margin: '9px 0 9px -8px',
  },
  addLine: {
    width: '100%',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    margin: '24px 0 12px 0',
  },
  totalDisplay: {
    width: 192,
    display: 'flex',
    flexDirection: 'column',
  },
}));

type keyOrClickEvent = Omit<React.KeyboardEvent<HTMLInputElement> | React.MouseEvent<HTMLInputElement>, 'target'> & {
  target: HTMLInputElement;
};

type focusEvent = Omit<React.FocusEvent, 'target'> & { target: HTMLInputElement | HTMLTextAreaElement };

type FormErrors = {
  name?: string;
  targetAmount?: string;
  contributions?: Array<null | { accountId?: string; amount?: string }>;
};
type FormTouched = {
  name?: boolean;
  targetAmount?: boolean;
  savedSoFar?: boolean;
  savedSoFarFromAccount?: boolean;
  submitted: boolean;
};

const GoalAmounts = () => {
  const { dispatch, state } = useContext(AddGoalContext);
  const { type, name, targetAmount, contributions } = state;
  const { classes } = useStyles();
  const allBalances = useAppSelector(balanceSelectors.getBalancesByAccountIds);
  const possibleContributionAccounts: ImMap<string, RecordOf<FundingAccount>> = useAppSelector(
    goalsSelectors.getPossibleContributionAccounts,
  );
  const goalsById: ImMap<string, Goal> = useSelector(goalsSelectors.getGoalsById);

  const [touched, setTouched] = useState<FormTouched>({
    name: false,
    targetAmount: false,
    savedSoFar: false,
    savedSoFarFromAccount: false,
    submitted: false,
  });
  const maxAbsoluteValue = 999999999.99;

  const totalAmount: number = contributions.reduce((value, contribution) => value + (contribution?.amount || 0), 0);

  // Callbacks
  const handleTargetChange = (e: keyOrClickEvent) => {
    dispatch({
      type: 'update',
      payload: {
        canCalculate: true,
        targetAmount: Number(e.target.value),
      },
    });
  };

  const handleNameChange = useCallback(
    (e: keyOrClickEvent) => {
      dispatch({ type: 'update', payload: { name: e.target.value } });
    },
    [dispatch],
  );

  const handleTouch = useCallback(
    (e: focusEvent) => {
      const targetName = e.target.getAttribute?.('name') || e.target?.name;
      if (targetName) {
        setTouched({ ...touched, [targetName]: true });
      }
    },
    [touched],
  );

  const handleAccountChange = (index: number) => (e: keyOrClickEvent) => {
    const currentItem = contributions[index];
    const newContributions = [...contributions];
    newContributions[index] = { accountId: e?.target?.value, amount: currentItem.amount };
    dispatch({
      type: 'update',
      payload: {
        canCalculate: true,
        contributions: newContributions,
      },
    });
  };

  const handleNumberChange = (index: number) => (e: keyOrClickEvent) => {
    const currentItem = contributions[index];
    const newContributions = [...contributions];
    newContributions[index] = { accountId: currentItem.accountId, amount: Number(e?.target?.value) };

    dispatch({
      type: 'update',
      payload: {
        canCalculate: true,
        contributions: newContributions,
      },
    });
  };

  const handleRemoveContribution = (index: number) => (_e?: any) => {
    const newContributions =
      contributions?.length > 1
        ? contributions.slice(0, index).concat(contributions.slice(index + 1))
        : [{ accountId: undefined, amount: 0 }];
    dispatch({
      type: 'update',
      payload: {
        canCalculate: true,
        contributions: newContributions,
      },
    });
  };

  const handleAddContribution = () => {
    dispatch({
      type: 'update',
      payload: {
        canCalculate: true,
        contributions: [...contributions, { accountId: undefined, amount: 0 }],
      },
    });
  };

  const errors: FormErrors = useMemo(() => {
    const newErrors: FormErrors = {};
    if (name.length === 0) {
      newErrors.name = 'Enter a goal name';
    }
    if (goalsById.find((g) => g.name?.toUpperCase() === name?.trim().toUpperCase())) {
      newErrors.name = 'Goal name must be unique';
    }
    if (targetAmount <= 0) {
      newErrors.targetAmount = 'Amount must be positive';
    }
    if (totalAmount > targetAmount) {
      newErrors.targetAmount = 'Goal amount must be more than total saved';
    }
    newErrors.contributions = contributions.map((contribution, index) => {
      const ret: { accountId?: string; amount?: string } = {};
      if (!contribution.accountId && !contribution.amount) {
        // if empty line, don't validate
        return null;
      }
      if (!contribution.accountId) {
        ret.accountId = 'Select the account where this amount is saved';
      } else if (
        contribution.amount &&
        contribution.accountId &&
        contribution.amount > (allBalances.get(contribution.accountId)?.availableBalance || 0)
      ) {
        ret.accountId = 'Not enough funds available in this account';
      } else if (contributions.find((value, i) => i !== index && value.accountId === contribution.accountId)) {
        ret.accountId = 'Cannot have duplicate accounts';
      }
      if (!contribution.amount) {
        ret.amount = 'Enter an amount saved';
      } else if (contribution.amount <= 0) {
        ret.amount = 'Amount saved must be positive';
      }

      return ret.accountId || ret.amount ? ret : null;
    });

    return newErrors;
  }, [name, goalsById, targetAmount, totalAmount, contributions, allBalances]);

  const isInvalid = useMemo(() => {
    return Boolean(errors.name || errors.targetAmount || errors.contributions?.some(Boolean));
  }, [errors]);

  const handleContinue = () => {
    if (isInvalid) {
      setTouched({ name: true, targetAmount: false, savedSoFar: true, savedSoFarFromAccount: true, submitted: true });
    } else {
      dispatch({
        type: 'update',
        payload: {
          tabIndex: 2,
          canCalculate: false,
          name: name?.trim(),
          savedSoFar: totalAmount,
        },
      });
    }
  };

  const propsForAccountSelect = useMemo(
    () => ({ MenuProps: { classes: { paper: classes.accountDropdownMenu } } }),
    [classes.accountDropdownMenu],
  );
  const propsForAccountInput = useMemo(() => ({ onBlur: handleTouch, name: 'savedSoFarFromAccount' }), [handleTouch]);

  const showNameError = touched.submitted && Boolean(errors.name);
  const showTargetError = touched.submitted && Boolean(errors.targetAmount);

  return (
    <>
      <div className={classes.goalAmountRoot}>
        <img className={classes.leftPanel} alt='goal-icon' src={type?.icon} />
        <Box className={classes.rightPanel} paddingRight={4}>
          <Typography variant='bodyRegular'>Tell us about your goal.</Typography>

          <TextField
            variant='filled'
            label='Goal Name'
            autoFocus
            value={name}
            name='name'
            onChange={handleNameChange}
            className={classes.nameField}
            id='create_goal_name'
            onBlur={handleTouch}
            error={showNameError}
            helperText={(showNameError && errors.name) || ''}
          />

          <QAmountField
            label='Goal Amount'
            textFieldVariant='filled'
            editable
            expectedSign='positive'
            onChange={handleTargetChange}
            value={targetAmount || 0}
            error={showTargetError}
            helperText={(showTargetError && errors.targetAmount) || ' '}
            id='create_goal_target_amount'
            name='targetAmount'
            onBlur={handleTouch}
            className={classes.amountField}
            maxVal={maxAbsoluteValue}
            minVal={-maxAbsoluteValue}
          />

          <Divider flexItem variant='middle' className={classes.divider} />

          <Stack direction='row'>
            <Typography variant='bodyRegular'>Enter any amount saved for this goal so far.</Typography>
            <InfoTooltipIcon tooltipText='Funds saved towards goals will appear separate from your available balance in these accounts. Use ‘+ Add Account’ to enter funds saved for this goal in more than one account.' />
          </Stack>

          {contributions.map((contribution, index) => {
            const rowErrors = errors.contributions?.[index];

            const showAccountError = Boolean(touched.savedSoFarFromAccount && rowErrors?.accountId);
            const showAmountError = Boolean(touched.savedSoFar && rowErrors?.amount);
            return (
              <div className={classes.savedSoFarWrapper} key={`wrapper-${index}-${contribution.accountId}$`}>
                <div className={classes.savedSoFarLine} key={`${index}-${contribution.accountId}$`}>
                  <TextField
                    variant='filled'
                    label='Saved in'
                    name='savedSoFarFromAccount'
                    className={classes.accountDropdownField}
                    value={contribution?.accountId || ''}
                    select
                    onChange={handleAccountChange(index)}
                    id={`create_goal_account_dropdown-${index}`}
                    error={showAccountError}
                    helperText={(showAccountError && rowErrors?.accountId) || ' '}
                    SelectProps={propsForAccountSelect}
                    InputProps={propsForAccountInput}
                  >
                    {possibleContributionAccounts.valueSeq?.().map?.((acct, index) =>
                      acct?.id ? (
                        <MenuItem
                          className={classes.accountDropdownItem}
                          key={acct.id ?? undefined}
                          value={acct.id ?? undefined}
                          id={`initial-contribution-${index}-account`}
                        >
                          <div className={classes.accountRow}>
                            <Typography noWrap maxWidth={320} minWidth={180}>
                              {acct.name}
                            </Typography>
                            <Typography variant='paragraphRegular'>
                              <AmountField
                                className={classes.accountAmount}
                                value={allBalances.get(acct.id)?.availableBalance || 0}
                              />
                            </Typography>
                          </div>
                        </MenuItem>
                      ) : (
                        <MenuItem key='deleted-acct' value='Deleted account'>
                          Deleted account
                        </MenuItem>
                      ),
                    )}
                  </TextField>

                  <QAmountField
                    label='Amount saved'
                    textFieldVariant='filled'
                    expectedSign='positive'
                    editable
                    onChange={handleNumberChange(index)}
                    value={contribution.amount || 0}
                    error={showAmountError}
                    helperText={(showAmountError && rowErrors?.amount) || ' '}
                    id={`initial-contribution-${index}-amount`}
                    name='savedSoFar'
                    onBlur={handleTouch}
                    className={classes.amountField}
                    maxVal={maxAbsoluteValue}
                    minVal={-maxAbsoluteValue}
                  />
                </div>
                {contributions?.length > 1 || contributions[0].accountId || contributions[0].amount ? (
                  <IconButton
                    aria-label={`remove contribution ${index}`}
                    id={`initial-contribution-${index}-remove`}
                    onClick={handleRemoveContribution(index)}
                    className={classes.deleteButton}
                  >
                    <CloseRounded />
                  </IconButton>
                ) : null}
              </div>
            );
          })}

          <div className={classes.addLine}>
            <QButton
              id='add-contribution'
              startIcon={<AddCircleRoundedIcon />}
              onClick={handleAddContribution}
              style={{ marginRight: '196px' }}
            >
              Add Account
            </QButton>

            {contributions?.length > 1 && (
              <div className={classes.totalDisplay}>
                <Typography variant='overlineBold'>TOTAL SAVED</Typography>
                <AmountField value={totalAmount || 0} />
              </div>
            )}
          </div>
        </Box>
      </div>

      <StdDialogActions
        primary={{
          children: 'Continue',
          onClick: handleContinue,
          variant: 'contained',
          id: 'create_goal_continue_button',
          showTooltip: isInvalid && touched.submitted,
        }}
      />
    </>
  );
};

export default GoalAmounts;
